import axios from 'axios-observable';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import CONFIG from '../../settings';
import { map } from 'rxjs/operators';
import type { TalosResponse, UUID } from '../app/basicModels';
import type { Characteristic, CharacteristicWithDefaultIndicator } from '../characteristic/models';
import type { ExtraDimension } from '../extraDimensions/models';
import type { Indicator } from '../indicator/models';
import type { Methodology } from '../methodology/models';
import type { MethodologyUpdatePayload } from '../methodologyDeep/models';
import type { Phenomenon, CloneCharacteristicsIndicatorsDTO, ClonedCharacteristicsIndicators } from '../phenomenon/models';
import type { PhenomenonCategory } from '../phenomenonCategory/models';
import {
  mapClonedIndicator,
  mapClonedPhenomenon,
  mapClonedPhenomenonEntities,
  mapPhenomenonEntitiesForClone,
  mapMethodologyForClone,
  mapCharacteristicForCreate,
  mapCharacteristicForUpdate,
  mapExtraDimensionForCreate,
  mapExtraDimensionForUpdate,
  mapIndicatorForCreate,
  mapIndicatorForUpdate,
  mapMethodologyForCreate,
  mapMethodologyForUpdate,
  mapPhenomenonForCreate,
  mapPhenomenonForUpdate,
  mapPhenomenonCategoryForCreate,
  mapPhenomenonCategoryForUpdate
} from './mappers';

const endpoint = (path: string): string => `${CONFIG.cupApiUrl}/v2/${path}`;

const isGlobalPhenomenonCategory = (category: PhenomenonCategory): boolean =>
  category.is_canonical === true && !category.company_id && !category.workspace_id;

export const ObservationsConfigV2 = {
  deletePhenomenonCategory(id: UUID): AxiosObservable<PhenomenonCategory> {
    return axios.delete(endpoint(`phenomenon-categories/${id}`));
  },

  deletePhenomenon(id: UUID): AxiosObservable<Phenomenon> {
    return axios.delete(endpoint(`phenomena/${id}`));
  },

  deleteCharacteristic(id: UUID): AxiosObservable<TalosResponse> {
    return axios.delete(endpoint(`characteristics/${id}`));
  },

  deleteIndicator(id: UUID): AxiosObservable<Indicator> {
    return axios.delete(endpoint(`indicators/${id}`));
  },

  deleteMethodology(id: UUID): AxiosObservable<Methodology> {
    return axios.delete(endpoint(`methodologies/${id}`));
  },

  clonePhenomenonToOrg(id: UUID, orgId: UUID): AxiosObservable<Phenomenon> {
    return axios
      .post<Phenomenon>(endpoint(`phenomena/${id}/clone-to-org/${orgId}`))
      .pipe(map(response => ({ ...response, data: mapClonedPhenomenon(response.data) })));
  },

  clonePhenomenonToWorkspace(id: UUID, workspaceId: UUID): AxiosObservable<Phenomenon> {
    return axios
      .post<Phenomenon>(endpoint(`phenomena/${id}/clone-to-workspace/${workspaceId}`))
      .pipe(map(response => ({ ...response, data: mapClonedPhenomenon(response.data) })));
  },

  cloneIndicatorToOrg(id: UUID, orgId: UUID): AxiosObservable<Indicator> {
    return axios
      .post<Indicator>(endpoint(`indicators/${id}/clone-to-org/${orgId}`))
      .pipe(map(response => ({ ...response, data: mapClonedIndicator(response.data) })));
  },

  cloneIndicatorToWorkspace(id: UUID, workspaceId: UUID): AxiosObservable<Indicator> {
    return axios
      .post<Indicator>(endpoint(`indicators/${id}/clone-to-workspace/${workspaceId}`))
      .pipe(map(response => ({ ...response, data: mapClonedIndicator(response.data) })));
  },

  clonePhenomenonEntities(items: CloneCharacteristicsIndicatorsDTO): AxiosObservable<ClonedCharacteristicsIndicators> {
    return axios
      .post<ClonedCharacteristicsIndicators>(endpoint('phenomena/clone-entities'), mapPhenomenonEntitiesForClone(items))
      .pipe(map(response => ({ ...response, data: mapClonedPhenomenonEntities(response.data) })));
  },

  cloneMethodologyToOrg(methodology: MethodologyUpdatePayload, id: UUID, orgId: UUID): AxiosObservable<Methodology> {
    return axios.post(endpoint(`methodologies/${id}/clone-to-org/${orgId}`), mapMethodologyForClone(methodology));
  },

  canWritePhenomenonCategory(category: PhenomenonCategory): boolean {
    return isGlobalPhenomenonCategory(category);
  },

  createPhenomenonCategory(category: PhenomenonCategory): AxiosObservable<PhenomenonCategory> {
    return axios.post<PhenomenonCategory>(endpoint('phenomenon-categories'), mapPhenomenonCategoryForCreate(category));
  },

  updatePhenomenonCategory(category: PhenomenonCategory): AxiosObservable<PhenomenonCategory> {
    return axios.put<PhenomenonCategory>(`${endpoint('phenomenon-categories')}/${category.id}`, mapPhenomenonCategoryForUpdate(category));
  },

  createPhenomenon(phenomenon: Phenomenon): AxiosObservable<Phenomenon> {
    return axios.post<Phenomenon>(endpoint('phenomena'), mapPhenomenonForCreate(phenomenon));
  },

  updatePhenomenon(phenomenon: Phenomenon): AxiosObservable<Phenomenon> {
    return axios.put<Phenomenon>(`${endpoint('phenomena')}/${phenomenon.id}`, mapPhenomenonForUpdate(phenomenon));
  },

  createCharacteristic(characteristic: Characteristic | CharacteristicWithDefaultIndicator): AxiosObservable<TalosResponse> {
    return axios.post<TalosResponse>(endpoint('characteristics'), mapCharacteristicForCreate(characteristic));
  },

  updateCharacteristic(characteristic: Characteristic): AxiosObservable<TalosResponse> {
    return axios.put<TalosResponse>(`${endpoint('characteristics')}/${characteristic.id}`, mapCharacteristicForUpdate(characteristic));
  },

  cloneCharacteristicToOrg(characteristicId: UUID, orgId: UUID): AxiosObservable<Characteristic> {
    return axios.post(endpoint(`characteristics/${characteristicId}/clone-to-org/${orgId}`));
  },

  cloneCharacteristicToWorkspace(characteristicId: UUID, workspaceId: UUID): AxiosObservable<Characteristic> {
    return axios.post(endpoint(`characteristics/${characteristicId}/clone-to-workspace/${workspaceId}`));
  },

  createIndicator(indicator: Indicator): AxiosObservable<Indicator> {
    return axios.post<Indicator>(endpoint('indicators'), mapIndicatorForCreate(indicator));
  },

  updateIndicator(indicator: Indicator): AxiosObservable<Indicator> {
    return axios.put<Indicator>(`${endpoint('indicators')}/${indicator.id}`, mapIndicatorForUpdate(indicator));
  },

  createExtraDimension(extraDimension: ExtraDimension): AxiosObservable<TalosResponse> {
    return axios.post<TalosResponse>(endpoint('extra-dimensions'), mapExtraDimensionForCreate(extraDimension));
  },

  updateExtraDimension(extraDimension: ExtraDimension): AxiosObservable<TalosResponse> {
    return axios.put<TalosResponse>(`${endpoint('extra-dimensions')}/${extraDimension.id}`, mapExtraDimensionForUpdate(extraDimension));
  },

  createMethodology(methodology: Methodology): AxiosObservable<Methodology> {
    return axios.post<Methodology>(endpoint('methodologies'), mapMethodologyForCreate(methodology));
  },

  updateMethodology(methodology: MethodologyUpdatePayload): AxiosObservable<Methodology> {
    return axios.put<Methodology>(`${endpoint('methodologies')}/${methodology.id}`, mapMethodologyForUpdate(methodology));
  }
};
